import { useMutation } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { Alert, Form as BsForm } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { postLogin } from '../../queries/postLogin.ts';
import { LoginPayload } from '../../schema/LoginPayload.ts';
import { setToken } from '../../utils/utils.ts';
import { Backlink, LoadingButton } from '../ui/Button.tsx';

export function LoginForm() {
  const navigate = useNavigate();
  const [loginFailed, setLoginFailed] = useState(false);
  const { t } = useTranslation();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      event.stopPropagation();
      return;
    }
    const formData = new FormData(event.currentTarget);
    mutation.mutate({
      username: formData.get('email') as string,
      password: formData.get('password') as string,
    });
  }

  const mutation = useMutation({
    mutationFn: (payload: LoginPayload) => postLogin(payload),
    onSuccess: async data => {
      setToken(data.data.token);
      navigate('/', { replace: true });
    },
    onError: () => {
      setLoginFailed(true);
    },
  });

  return (
    <BsForm onSubmit={onSubmit}>
      {loginFailed && (
        <Alert className={'mt-3'} variant="danger" onClose={() => setLoginFailed(false)} dismissible>
          <p className={'login-error'}>{t('login.error')}</p>
        </Alert>
      )}
      <BsForm.Group className={'mt-5'}>
        <BsForm.Control
          required
          type="text"
          name="email"
          placeholder={t('login.email')}
          className={'login-control shadow-sm'}
        />
        <BsForm.Control.Feedback type="invalid">{t('login.email-error')}</BsForm.Control.Feedback>
      </BsForm.Group>
      <BsForm.Group className={'mt-3'}>
        <BsForm.Control
          required
          type="password"
          name="password"
          placeholder={t('login.password')}
          className={'login-control shadow-sm'}
        />
        <BsForm.Control.Feedback type="invalid">{t('login.password-error')}</BsForm.Control.Feedback>
      </BsForm.Group>
      <div className="mt-1">
        <Backlink text={t('login.forgot')} to={`/reset-password/`} style={'text-hyperlink'} />
      </div>
      <div className={'mb-1 mt-4 text-center'}>
        <LoadingButton
          type={'submit-button'}
          text={t('login.submit')}
          imagePath={null}
          loading={mutation.isPending}
          onClick={undefined}
        />
      </div>
    </BsForm>
  );
}
